/* iter-loom · Loom Studio dashboard.
 *
 * One page, four tabs — the Loom control panel for creating
 * pseudonyms, browsing the 24-novel seed spec, dispatching manuscripts
 * to draft, and reviewing the approved library. Admin-only (route is
 * gated behind admin login).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { loomBlurbApi } from '@/lib/api';
import { PanelHeader } from './admin/_shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  BookOpen, UserSquare, Palette, Library, Loader2, Sparkles, ImagePlus,
  PenLine,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ax = axios.create({ baseURL: API, withCredentials: true });

const BUCKET_META = {
  contemp: { label: 'Contemp',  tone: 'bg-rose-100 text-rose-900 dark:bg-rose-950/40' },
  dark:    { label: 'Dark',     tone: 'bg-red-100 text-red-900 dark:bg-red-950/40' },
  grey:    { label: 'Grey',     tone: 'bg-stone-200 text-stone-900 dark:bg-stone-800/70' },
  scifi:   { label: 'Sci-Fi',   tone: 'bg-indigo-100 text-indigo-900 dark:bg-indigo-950/40' },
  sweet:   { label: 'Sweet',    tone: 'bg-amber-100 text-amber-900 dark:bg-amber-950/40' },
};

// ------------------------- Pseudonyms -------------------------------

function PseudonymsTab({ pseudonyms, onCreated, taxonomy }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ display_name: '', bio: '',
                                      age_bracket: '30s', style_notes: '' });
  const submit = async () => {
    setBusy(true);
    try {
      await ax.post('/loom/pseudonyms', form);
      setOpen(false);
      setForm({ display_name: '', bio: '', age_bracket: '30s', style_notes: '' });
      onCreated();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3" data-testid="loom-pseudonyms-tab">
      <div className="flex items-center justify-between">
        <div className="text-[12.5px] text-muted-foreground">
          {pseudonyms.length} pseudonym{pseudonyms.length === 1 ? '' : 's'} — each is a fictional female author with an AI-generated headshot.
        </div>
        <Button size="sm" onClick={() => setOpen(true)}
                data-testid="loom-new-pseudonym">
          <ImagePlus className="h-3.5 w-3.5 mr-1" /> New pseudonym
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {pseudonyms.map((p) => (
          <div key={p.id} className="rounded-md border border-border bg-card p-3"
               data-testid={`loom-pseudonym-${p.id}`}>
            {p.headshot_url ? (
              <img src={API + p.headshot_url.replace(/^\/api/, '')}
                    alt={p.display_name}
                    className="mb-2 aspect-[4/5] w-full rounded object-cover" />
            ) : (
              <div className="mb-2 aspect-[4/5] w-full rounded bg-muted flex items-center justify-center text-muted-foreground text-[11px]">
                (portrait pending)
              </div>
            )}
            <div className="font-serif text-[13px]">{p.display_name}</div>
            <div className="text-[10.5px] text-muted-foreground">
              {p.age_bracket} · {p.style_notes || 'no notes'}
            </div>
          </div>
        ))}
        {!pseudonyms.length && (
          <div className="col-span-full rounded-md border border-dashed p-8 text-center text-[12px] text-muted-foreground">
            No pseudonyms yet. Create the first one above.
          </div>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="loom-pseudonym-dialog">
          <DialogHeader><DialogTitle>New pseudonym</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Display name</Label>
              <Input value={form.display_name}
                     onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                     placeholder="Adeline Vance"
                     data-testid="loom-pseudonym-name" />
            </div>
            <div className="flex gap-3">
              {['20s', '30s', '40s', '50s', '60s'].map((a) => (
                <Button key={a} size="sm"
                        variant={form.age_bracket === a ? 'default' : 'outline'}
                        onClick={() => setForm({ ...form, age_bracket: a })}
                        data-testid={`loom-age-${a}`}>{a}</Button>
              ))}
            </div>
            <div>
              <Label>Style notes (optional)</Label>
              <Input value={form.style_notes}
                     onChange={(e) => setForm({ ...form, style_notes: e.target.value })}
                     placeholder="warm, literary, hint of a smile" />
            </div>
            <div>
              <Label>Short bio</Label>
              <Textarea value={form.bio}
                        onChange={(e) => setForm({ ...form, bio: e.target.value })}
                        rows={3}
                        placeholder="Ex-newspaper columnist writing quiet stories about long marriages." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.display_name.trim()}
                    data-testid="loom-pseudonym-submit">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              Create + generate portrait
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------- Idea Board / Specs -----------------------

function IdeaBoardTab({ specs, taxonomy, pseudonyms, onCreated }) {
  const [openIndex, setOpenIndex] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pseudonymId, setPseudonymId] = useState('');

  const idLookup = useMemo(() => {
    const out = {};
    ['tropes', 'actors', 'stressors', 'vulnerabilities'].forEach((k) => {
      (taxonomy?.[k] || []).forEach((it) => { out[it.id] = it.label; });
    });
    return out;
  }, [taxonomy]);

  const dispatch = async () => {
    if (openIndex == null || !pseudonymId) return;
    setBusy(true);
    try {
      await ax.post('/loom/manuscripts', {
        spec_index:   openIndex,
        pseudonym_id: pseudonymId,
      });
      setOpenIndex(null);
      setPseudonymId('');
      onCreated();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="loom-ideaboard-tab">
      <div className="text-[12.5px] text-muted-foreground">
        24-novel seed spec — click any card to review its beats, then dispatch to a pseudonym.
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {specs.map((s, i) => (
          <button key={i}
                  onClick={() => setOpenIndex(i)}
                  data-testid={`loom-spec-${i}`}
                  className="text-left rounded-md border border-border bg-card p-3 hover:border-primary/40 transition">
            <div className="flex items-center justify-between gap-2">
              <Badge className={`text-[10px] uppercase ${BUCKET_META[s.bucket]?.tone || ''}`}>
                {BUCKET_META[s.bucket]?.label || s.bucket}
              </Badge>
              <span className="text-[10px] uppercase text-muted-foreground">{s.heat}</span>
            </div>
            <div className="mt-1.5 font-serif text-[14px] leading-tight">{s.title}</div>
            {s.logline && (
              <div className="mt-1 text-[11.5px] text-muted-foreground line-clamp-3">
                {s.logline}
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-1">
              {(s.tropes || []).slice(0, 3).map((t) => (
                <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-[10px]">
                  {idLookup[t] || t}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>
      <Dialog open={openIndex != null} onOpenChange={(v) => (!v ? setOpenIndex(null) : null)}>
        <DialogContent data-testid="loom-spec-dialog">
          {openIndex != null && (
            <>
              <DialogHeader>
                <DialogTitle>{specs[openIndex]?.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-[12.5px]">
                <div className="text-muted-foreground">{specs[openIndex]?.logline}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Tropes</div>
                    {(specs[openIndex]?.tropes || []).map((t) => (
                      <div key={t}>{idLookup[t] || t}</div>
                    ))}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Actors</div>
                    {Object.entries(specs[openIndex]?.actors || {}).map(([role, id]) => (
                      <div key={role}><b>{role}:</b> {idLookup[id] || id}</div>
                    ))}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Stressors</div>
                    {(specs[openIndex]?.stressors || []).map((t) => (
                      <div key={t}>{idLookup[t] || t}</div>
                    ))}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Vulnerabilities</div>
                    {(specs[openIndex]?.vulnerabilities || []).map((t) => (
                      <div key={t}>{idLookup[t] || t}</div>
                    ))}
                  </div>
                </div>
                <div className="rounded bg-muted/40 p-2">
                  <div className="text-[10px] uppercase text-muted-foreground mb-1">Assign pseudonym</div>
                  <select
                    value={pseudonymId}
                    onChange={(e) => setPseudonymId(e.target.value)}
                    className="w-full rounded border border-border bg-background p-2 text-[12px]"
                    data-testid="loom-spec-pseudonym-select"
                  >
                    <option value="">— pick an author —</option>
                    {pseudonyms.map((p) => (
                      <option key={p.id} value={p.id}>{p.display_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpenIndex(null)}>Cancel</Button>
                <Button onClick={dispatch} disabled={busy || !pseudonymId}
                        data-testid="loom-spec-dispatch">
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                  Dispatch to draft
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------- Manuscripts + Library -------------------

function ManuscriptCover({ m }) {
  // Frontend-only cover thumbnail. Renders m.cover_front_url when set,
  // else a small parchment placeholder so proofers see a slot for the
  // Nano-Banana cover even before it lands.
  const [failed, setFailed] = useState(false);
  if (m.cover_front_url && !failed) {
    return (
      <img src={m.cover_front_url}
           alt={`${m.spec_title} cover`}
           onError={() => setFailed(true)}
           className="h-14 w-10 shrink-0 rounded-sm border border-border object-cover shadow-sm"
           data-testid={`loom-cover-${m.id}`} />
    );
  }
  return (
    <div className="flex h-14 w-10 shrink-0 flex-col items-center justify-center rounded-sm border border-dashed border-border/70 bg-muted/30 text-[8px] uppercase tracking-wide text-muted-foreground"
         data-testid={`loom-cover-placeholder-${m.id}`}>
      <BookOpen className="h-3.5 w-3.5 mb-0.5 opacity-60" />
      <span>Cover</span>
      <span>pending</span>
    </div>
  );
}

function ManuscriptsTab({ manuscripts, onApprove, onDraft, onBlurb, blurbing, blurbBulk, drafting }) {
  if (!manuscripts.length) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-[12px] text-muted-foreground"
           data-testid="loom-manuscripts-empty">
        No manuscripts yet. Dispatch one from the Idea Board.
      </div>
    );
  }
  const missingCount = manuscripts.filter((m) => !m.back_copy).length;
  return (
    <div className="space-y-2" data-testid="loom-manuscripts-tab">
      {missingCount > 0 && (
        <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
             data-testid="loom-blurb-bulk-banner">
          <span>
            <b>{missingCount}</b> manuscript{missingCount === 1 ? '' : 's'} without a back-cover blurb.
          </span>
          <Button size="sm" variant="outline" disabled={blurbBulk}
                  onClick={onBlurb.bulk}
                  data-testid="loom-blurb-bulk-btn">
            {blurbBulk
              ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              : <PenLine className="mr-1 h-3.5 w-3.5" />}
            Draft missing blurbs
          </Button>
        </div>
      )}
      {manuscripts.map((m) => (
        <div key={m.id}
             className="rounded-md border border-border bg-card p-3 text-[12.5px]"
             data-testid={`loom-manuscript-${m.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <ManuscriptCover m={m} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-serif text-[13.5px]">{m.spec_title}</span>
                <Badge className={`text-[10px] uppercase ${BUCKET_META[m.spec_bucket]?.tone || ''}`}>
                  {BUCKET_META[m.spec_bucket]?.label || m.spec_bucket}
                </Badge>
                <Badge variant="outline" className="text-[10px]">{m.status}</Badge>
                {m.status === 'drafting' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9.5px] font-medium uppercase text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                        data-testid={`loom-drafting-pulse-${m.id}`}>
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-600" />
                    live
                  </span>
                )}
                <span className="text-[11px] text-muted-foreground">
                  by <b>{m.pseudonym_name || m.pseudonym_id}</b>
                </span>
                {m.back_copy && (
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9.5px] font-medium uppercase text-emerald-800"
                        title={m.back_copy}
                        data-testid={`loom-blurb-badge-${m.id}`}>
                    blurb
                  </span>
                )}
              </div>
              {(m.warnings || []).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {m.warnings.map((w) => (
                    <span key={w} className="rounded bg-red-100 text-red-900 px-1.5 py-0.5 text-[10px] dark:bg-red-950/40 dark:text-red-200">
                      {w}
                    </span>
                  ))}
                </div>
              )}
              {m.back_copy && (
                <p className="mt-1 line-clamp-2 text-[11px] italic text-muted-foreground"
                   data-testid={`loom-blurb-preview-${m.id}`}>
                  {m.back_copy}
                </p>
              )}
              {m.build_job_id && (
                <div className="mt-1 text-[10.5px] font-mono text-muted-foreground">
                  job: {m.build_job_id}
                  {m.model_id && <span className="ml-2 rounded bg-muted px-1 py-0.5 text-[10px] font-mono">{m.model_id}</span>}
                </div>
              )}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline"
                      disabled={blurbing === m.id}
                      onClick={() => onBlurb.one(m)}
                      title={m.back_copy ? 'Regenerate back-cover blurb' : 'Draft back-cover blurb'}
                      data-testid={`loom-blurb-${m.id}`}>
                {blurbing === m.id
                  ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  : <PenLine className="mr-1 h-3.5 w-3.5" />}
                {m.back_copy ? 'Redraft' : 'Blurb'}
              </Button>
              {m.status === 'draft' && (
                <Button size="sm" variant="default"
                        disabled={drafting === m.id}
                        onClick={() => onDraft(m)}
                        data-testid={`loom-draft-${m.id}`}>
                  {drafting === m.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5 mr-1" />
                  )}
                  Draft
                </Button>
              )}
              {m.status === 'draft' && (
                <Button size="sm" variant="outline"
                        onClick={() => onApprove(m)}
                        data-testid={`loom-approve-${m.id}`}>
                  Approve
                </Button>
              )}
              {(m.status === 'drafting' || m.status === 'in_review') && (
                <Button size="sm" variant="outline"
                        onClick={() => onApprove(m)}
                        data-testid={`loom-approve-${m.id}`}>
                  Approve
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------- Main page --------------------------------

export default function LoomPage() {
  const [tab, setTab] = useState('ideaboard');
  const [taxonomy, setTaxonomy] = useState(null);
  const [specs, setSpecs] = useState([]);
  const [pseudonyms, setPseudonyms] = useState([]);
  const [manuscripts, setManuscripts] = useState([]);
  const [status, setStatus] = useState(null);
  const [drafting, setDrafting] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [models, setModels] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [costMeter, setCostMeter] = useState(null);
  const [publishingAll, setPublishingAll] = useState(false);
  const [draftDialog, setDraftDialog] = useState({ open: false, ms: null });
  const [batchDrafting, setBatchDrafting] = useState(null); // {done, total, current}
  const [wallet, setWallet] = useState(null); // {balance_cents, markup_pct} · iter-track-b
  const [blurbing, setBlurbing] = useState(null);   // Feb-2026 · single-book blurb spinner
  const [blurbBulk, setBlurbBulk] = useState(false); // Feb-2026 · bulk-missing spinner

  const refresh = useCallback(async () => {
    const [tx, sp, ps, ms, st, mods, dfl, cm, wb] = await Promise.all([
      ax.get('/loom/taxonomy').then((x) => x.data).catch(() => null),
      ax.get('/loom/specs').then((x) => x.data?.specs || []).catch(() => []),
      ax.get('/loom/pseudonyms').then((x) => x.data?.pseudonyms || []).catch(() => []),
      ax.get('/loom/manuscripts').then((x) => x.data?.manuscripts || []).catch(() => []),
      ax.get('/loom/bootstrap-status').then((x) => x.data).catch(() => null),
      ax.get('/loom/models').then((x) => x.data).catch(() => null),
      ax.get('/admin/loom/defaults').then((x) => x.data).catch(() => null),
      ax.get('/loom/cost-meter').then((x) => x.data).catch(() => null),
      ax.get('/admin/wallet/summary').then((x) => x.data).catch(() => null),
    ]);
    setTaxonomy(tx); setSpecs(sp); setPseudonyms(ps);
    setManuscripts(ms); setStatus(st);
    setModels(mods?.models || []); setDefaults(dfl); setCostMeter(cm);
    setWallet(wb);  // iter-track-b · AI Spend Wallet strip
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Track A · Live Progress Ribbon: auto-refresh every 30 s while any
  // manuscript is in `drafting` state so words-per-hour + credit spend
  // tick in real-time without a manual reload. Cleans up on unmount.
  useEffect(() => {
    const hasLive = manuscripts.some((m) => m.status === 'drafting');
    if (!hasLive) return;
    const iv = setInterval(() => { refresh(); }, 30000);
    return () => clearInterval(iv);
  }, [manuscripts, refresh]);

  const onApprove = async (m) => {
    const note = window.prompt('Approval note (optional):', '') || '';
    try {
      await ax.post(`/loom/manuscripts/${m.id}/approve`, { note });
      await refresh();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const onDraft = (m) => setDraftDialog({ open: true, ms: m });

  // Feb-2026 — Blurb generator handlers (Claude Sonnet via Emergent LLM).
  const onBlurbOne = async (m) => {
    setBlurbing(m.id);
    try {
      const r = await loomBlurbApi.generate(m.id, { style: 'hook', overwrite: true });
      if (r?.blurb) {
        toast.success(`Drafted blurb for "${m.spec_title}" (${r.blurb.length} chars).`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Blurb generation failed.');
    } finally { setBlurbing(null); }
  };
  const onBlurbBulk = async () => {
    const missing = manuscripts.filter((m) => !m.back_copy).length;
    if (!missing) { toast.info('No manuscripts missing a blurb.'); return; }
    if (!window.confirm(
      `Draft blurbs for ${Math.min(missing, 20)} manuscript(s) sequentially? `
      + `Each call runs Claude Sonnet against the Emergent LLM key.`
    )) return;
    setBlurbBulk(true);
    try {
      const r = await loomBlurbApi.bulk({ style: 'hook', limit: 20 });
      toast.success(`Generated ${r.generated} blurb(s), ${r.failed} failed.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk blurb run failed.');
    } finally { setBlurbBulk(false); }
  };
  const onBlurb = { one: onBlurbOne, bulk: onBlurbBulk };

  const runDraft = async ({ model_id, revision_passes }) => {
    const m = draftDialog.ms;
    if (!m) return;
    setDrafting(m.id);
    try {
      await ax.post(`/loom/manuscripts/${m.id}/draft`, {
        mode: 'sequential',
        model_id,
        revision_passes,
      });
      setDraftDialog({ open: false, ms: null });
      await refresh();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDrafting(null);
    }
  };

  const saveDefaults = async (patch) => {
    try {
      await ax.put('/admin/loom/defaults', patch);
      await refresh();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Track A · Batch Draft-All — one-click sequential dispatch of every
  // manuscript still in `draft` status. Uses the existing per-book
  // draft endpoint so each POST honours model-override + revision plan.
  // A single 409 mid-batch (build already active) is skipped rather
  // than halting the queue.
  const batchDraftAll = async () => {
    const queue = manuscripts.filter((m) => m.status === 'draft');
    if (!queue.length) { alert('No manuscripts in "draft" status.'); return; }
    if (!window.confirm(
      `Kick off drafting for ${queue.length} manuscript(s) sequentially? ` +
      `Each will use its saved model + revision setting.`
    )) return;
    setBatchDrafting({ done: 0, total: queue.length, current: queue[0]?.spec_title || '' });
    let done = 0;
    for (const m of queue) {
      setBatchDrafting({ done, total: queue.length, current: m.spec_title });
      try {
        await ax.post(`/loom/manuscripts/${m.id}/draft`, {
          mode: 'sequential',
          model_id: m.model_id || undefined,
          revision_passes: m.revision_passes || 0,
        });
      } catch (e) {
        // 409 = already running — carry on. Other failures logged.
        if (e?.response?.status !== 409) {
          console.warn(`[loom-batch] ${m.spec_title} failed:`, e?.response?.data?.detail);
        }
      }
      done += 1;
    }
    setBatchDrafting(null);
    await refresh();
    alert(`Dispatched ${done} manuscript(s). Watch the Manuscripts tab for progress.`);
  };

  const publishAllApproved = async () => {
    const approvedCount = manuscripts.filter((m) => m.status === 'approved').length;
    if (!approvedCount) {
      alert('No approved manuscripts to publish.');
      return;
    }
    if (!window.confirm(
      `Publish ${approvedCount} approved manuscript(s) to the bookstore? Each auto-maps its genre from the seed bucket.`
    )) return;
    setPublishingAll(true);
    try {
      const r = await ax.post('/loom/manuscripts/publish-all-approved',
                                { price_cents: 499, is_visible: true });
      const d = r.data || {};
      alert(`Published ${d.published} of ${d.attempted}. ${d.failed} failed.`);
      await refresh();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPublishingAll(false);
    }
  };

  const onSeedDefaults = async () => {
    if (!window.confirm(
      'Seed 6 default pseudonyms with AI-generated portraits (~$0.24 in Nano Banana calls)?'
    )) return;
    setSeeding(true);
    try {
      const r = await ax.post('/loom/pseudonyms/seed-defaults', {});
      alert(`Created ${r.data.created.length}, skipped ${r.data.skipped.length}.`);
      await refresh();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="mx-auto h-full max-w-6xl overflow-y-auto p-6" data-testid="loom-page">
      <PanelHeader
        title="Loom Studio"
        subtitle="AI-author control panel — idea board, pseudonyms, manuscripts, and the approved library."
        onRefresh={refresh}
      />
      {status && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-5 text-[11.5px]">
          <div className="rounded border border-border bg-card p-2" data-testid="loom-stat-loom-user">
            <div className="text-[10px] uppercase text-muted-foreground">Loom user</div>
            <div className="font-mono text-[11px]">{status.loom_user_id}</div>
          </div>
          <div className="rounded border border-border bg-card p-2" data-testid="loom-stat-pseudonyms">
            <div className="text-[10px] uppercase text-muted-foreground">Pseudonyms</div>
            <div className="font-mono-stat text-[16px] font-semibold">{status.pseudonym_count}</div>
          </div>
          <div className="rounded border border-border bg-card p-2" data-testid="loom-stat-manuscripts">
            <div className="text-[10px] uppercase text-muted-foreground">Manuscripts</div>
            <div className="font-mono-stat text-[16px] font-semibold">{status.manuscript_count}</div>
          </div>
          <div className="rounded border border-border bg-card p-2" data-testid="loom-stat-specs">
            <div className="text-[10px] uppercase text-muted-foreground">Seed specs</div>
            <div className="font-mono-stat text-[16px] font-semibold">{status.spec_count}</div>
          </div>
          <div className="rounded border border-red-200 bg-red-50/60 p-2 dark:bg-red-950/20" data-testid="loom-stat-costmeter">
            <div className="text-[10px] uppercase text-red-800/70 dark:text-red-300">Loom spend</div>
            <div className="font-mono-stat text-[16px] font-semibold text-red-900 dark:text-red-200">
              ${(costMeter?.today?.usd || 0).toFixed(2)}
              <span className="ml-1 text-[10px] font-normal text-red-800/70">today</span>
            </div>
            <div className="text-[10.5px] text-red-800/70 dark:text-red-300/80">
              7d ${(costMeter?.last_7_days?.usd || 0).toFixed(2)} ·
              all ${(costMeter?.all_time?.usd || 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* iter-track-b · AI Spend Wallet strip. Shows the marked-up
          balance only (no raw cost exposure). "Add funds →" deep-links
          to Finances panel where admins can top up via Stripe. */}
      {wallet && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 dark:border-emerald-900/40 dark:bg-emerald-950/20"
             data-testid="loom-wallet-strip">
          <div className="flex flex-wrap items-center gap-3 text-[11.5px]">
            <span className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${
                wallet.balance_cents <= 0
                  ? 'bg-red-600 animate-pulse'
                  : wallet.balance_cents < 1000
                    ? 'bg-amber-600'
                    : 'bg-emerald-600'
              }`} />
              <b>AI Spend Wallet:</b>
            </span>
            <span className="font-mono-stat text-[13px] font-semibold text-emerald-900 dark:text-emerald-200">
              ${(wallet.balance_cents / 100).toFixed(2)}
            </span>
            {wallet.balance_cents <= 0 && (
              <span className="rounded bg-red-100 px-1.5 py-0.5 text-[9.5px] font-medium uppercase text-red-900 dark:bg-red-950/40 dark:text-red-200">
                empty — drafts paused
              </span>
            )}
            {wallet.balance_cents > 0 && wallet.balance_cents < 1000 && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9.5px] font-medium uppercase text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                low balance
              </span>
            )}
          </div>
          <a href="/admin/finance"
             className="rounded border border-emerald-400 bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-900 hover:bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-100"
             data-testid="loom-wallet-add-funds">
            + Add funds →
          </a>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="ideaboard" className="gap-1.5" data-testid="loom-tab-ideaboard">
            <Palette className="h-3.5 w-3.5" /> Idea Board
          </TabsTrigger>
          <TabsTrigger value="pseudonyms" className="gap-1.5" data-testid="loom-tab-pseudonyms">
            <UserSquare className="h-3.5 w-3.5" /> Pseudonyms
          </TabsTrigger>
          <TabsTrigger value="manuscripts" className="gap-1.5" data-testid="loom-tab-manuscripts">
            <BookOpen className="h-3.5 w-3.5" /> Manuscripts
            {manuscripts.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">{manuscripts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="library" className="gap-1.5" data-testid="loom-tab-library">
            <Library className="h-3.5 w-3.5" /> Library
          </TabsTrigger>
        </TabsList>
        <TabsContent value="ideaboard" className="mt-3">
          <IdeaBoardTab specs={specs} taxonomy={taxonomy}
                          pseudonyms={pseudonyms} onCreated={refresh} />
        </TabsContent>
        <TabsContent value="pseudonyms" className="mt-3">
          <div className="mb-2">
            <Button size="sm" variant="outline" disabled={seeding}
                    onClick={onSeedDefaults}
                    data-testid="loom-seed-defaults">
              {seeding && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              Seed 6 default pseudonyms
            </Button>
          </div>
          <PseudonymsTab pseudonyms={pseudonyms} taxonomy={taxonomy}
                          onCreated={refresh} />
        </TabsContent>
        <TabsContent value="manuscripts" className="mt-3">
          {/* Track A · Live Progress Ribbon — shows drafting count,
              approved count, and running credit spend. Only renders
              when at least one manuscript is drafting so it stays out
              of the way when the shelf is idle. */}
          {(() => {
            const draftingCount  = manuscripts.filter((m) => m.status === 'drafting').length;
            const approvedCount  = manuscripts.filter((m) => m.status === 'approved').length;
            const publishedCount = manuscripts.filter((m) => m.status === 'published').length;
            if (!draftingCount && !batchDrafting) return null;
            return (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-950/20"
                   data-testid="loom-live-progress-ribbon">
                <div className="flex flex-wrap items-center gap-3 text-[11.5px]">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-amber-600" />
                    <b>{draftingCount}</b> drafting
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span><b>{approvedCount}</b> approved</span>
                  <span className="text-muted-foreground">·</span>
                  <span><b>{publishedCount}</b> published</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="font-mono">
                    ${(costMeter?.today?.usd || 0).toFixed(2)} today
                    <span className="ml-1 text-muted-foreground">
                      / ${(costMeter?.all_time?.usd || 0).toFixed(2)} all-time
                    </span>
                  </span>
                </div>
                {batchDrafting && (
                  <div className="flex items-center gap-2 text-[11px] text-amber-900 dark:text-amber-200"
                       data-testid="loom-batch-drafting-progress">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Dispatching {batchDrafting.done + 1} / {batchDrafting.total}:
                    <span className="font-serif italic">{batchDrafting.current}</span>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-[11.5px] text-muted-foreground">
              {manuscripts.filter((m) => m.status === 'approved').length} approved · ready to publish
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="secondary"
                      disabled={!!batchDrafting || !manuscripts.some((m) => m.status === 'draft')}
                      onClick={batchDraftAll}
                      data-testid="loom-draft-all">
                {batchDrafting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                <Sparkles className="h-3.5 w-3.5 mr-1" />
                Draft all {manuscripts.filter((m) => m.status === 'draft').length}
              </Button>
              <Button size="sm" variant="default"
                      disabled={publishingAll || !manuscripts.some((m) => m.status === 'approved')}
                      onClick={publishAllApproved}
                      data-testid="loom-publish-all">
                {publishingAll && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Publish all approved →
              </Button>
            </div>
          </div>
          <ManuscriptsTab manuscripts={manuscripts} onApprove={onApprove}
                            onDraft={onDraft} drafting={drafting}
                            onBlurb={onBlurb}
                            blurbing={blurbing} blurbBulk={blurbBulk} />
        </TabsContent>
        <TabsContent value="library" className="mt-3">
          <ManuscriptsTab
            manuscripts={manuscripts.filter((m) => m.status === 'approved' || m.status === 'published')}
            onApprove={() => {}}
            onDraft={() => {}}
            drafting={null}
            onBlurb={onBlurb}
            blurbing={blurbing}
            blurbBulk={blurbBulk}
          />
        </TabsContent>
      </Tabs>

      <DraftDialog
        open={draftDialog.open}
        onOpenChange={(v) => (!v ? setDraftDialog({ open: false, ms: null }) : null)}
        manuscript={draftDialog.ms}
        models={models}
        defaults={defaults}
        onConfirm={runDraft}
        onSaveDefaults={saveDefaults}
      />
    </div>
  );
}

function DraftDialog({ open, onOpenChange, manuscript, models, defaults, onConfirm, onSaveDefaults }) {
  const [modelId, setModelId] = useState('');
  const [passes, setPasses]   = useState(0);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setModelId(defaults?.model_id || 'claude-sonnet-4-5');
      setPasses(defaults?.revision_passes ?? 0);
      setSaveAsDefault(false);
    }
  }, [open, defaults]);

  if (!manuscript) return null;
  const est = estimatedCost(models, modelId, passes, manuscript);

  const confirm = async () => {
    setBusy(true);
    try {
      if (saveAsDefault) {
        await onSaveDefaults({ model_id: modelId, revision_passes: passes });
      }
      await onConfirm({ model_id: modelId, revision_passes: passes });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="loom-draft-dialog">
        <DialogHeader>
          <DialogTitle>Draft "{manuscript.spec_title}"</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[12.5px]">
          <div>
            <Label className="text-[11px] uppercase tracking-wide">Model</Label>
            <select
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              className="mt-1 w-full rounded border border-border bg-background p-2 text-[12px]"
              data-testid="loom-draft-model"
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label} — {m.cost_tier} · {m.recommended_for}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide">Revision passes</Label>
            <div className="mt-1 flex gap-2">
              {[0, 1, 2, 3].map((n) => (
                <Button key={n} size="sm"
                        variant={passes === n ? 'default' : 'outline'}
                        onClick={() => setPasses(n)}
                        data-testid={`loom-draft-passes-${n}`}>{n}</Button>
              ))}
            </div>
            <p className="mt-1 text-[10.5px] text-muted-foreground">
              Revision passes sharpen the mirror scene, climax, and dénouement. 2 is the sweet spot for the emotional payoff you're after.
            </p>
          </div>
          <div className="rounded bg-muted/40 p-2">
            <div className="text-[10px] uppercase text-muted-foreground">Estimated cost</div>
            <div className="font-mono-stat text-[16px] font-semibold">~${est.toFixed(2)}</div>
            <div className="text-[10.5px] text-muted-foreground">
              Rough — actual usage tracked in Admin → Unit Economics.
            </div>
          </div>
          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={saveAsDefault}
                    onChange={(e) => setSaveAsDefault(e.target.checked)}
                    data-testid="loom-draft-save-defaults" />
            Save these as the Loom defaults
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={confirm} disabled={busy}
                  data-testid="loom-draft-confirm">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
            Start draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function estimatedCost(models, modelId, passes, ms) {
  const m = (models || []).find((x) => x.id === modelId);
  if (!m) return 0;
  // Sonnet 4.5 baseline: ~$3 per novel drafted end-to-end with 0 passes.
  // Scale by the model's cost_multiplier and add ~30% per revision pass.
  const base = 3.0 * (m.cost_multiplier || 1.0);
  return base * (1 + 0.3 * (passes || 0));
}
